import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FileStatus } from "@air/feature-file-status/constants";
import { FileStatusPanelHeader, FileStatusTrackingPanelList, FileStatusTrackingPanelListItemAborted, FileStatusTrackingPanelListItemCompleted, FileStatusTrackingPanelListItemErrored, FileStatusTrackingPanelListItemPaused, FileStatusTrackingPanelListItemProgressing } from "@air/feature-file-status/ui";
import { UploadStatus } from "@air/redux-uploader";
import { tailwindMerge } from "@air/tailwind-variants";
import noop from "lodash/noop";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
const UPLOAD_PANE_WIDTH = 370;
export const progressingStatuses = [
    UploadStatus.preparing,
    UploadStatus.uploading,
    UploadStatus.queued
];
const getSummaryTrackingStatus = (param)=>{
    let { erroredCount, progressingCount, pausedCount } = param;
    if (pausedCount > 0 && !progressingCount) {
        return FileStatus.paused;
    } else if (progressingCount) {
        return FileStatus.progressing;
    } else if (erroredCount) {
        return FileStatus.errored;
    } else {
        return FileStatus.completed;
    }
};
// right margin of uploader pane is 16, so for screens smaller than UPLOAD_PANE_WIDTH set window.innerWidth - 2*this margin
const minWidth = "object" === "undefined" ? 0 : window.innerWidth - 32;
export const FileStatusTrackingPanel = /*#__PURE__*/ memo((param)=>{
    let { className, disableCancel, onCancelAll, onDismiss, onPauseAll, onResumeAll, onRetryAll, items, onCancelItem, onPauseItem, onResumeItem, onRetryItem, testId, isExpanded = false, onExpandToggled = noop, getStatusTitles, style, showAborted = false } = param;
    /**
     * This effectively does the same thing as if `useState` initial state was set to `false`
     * Through to `toggleUploaderContent` and `setUploaderOpen` in the `useEffect`
     */ const [isUploaderOpen, setUploaderOpen] = useState(isExpanded);
    const toggleUploaderContent = useCallback(()=>{
        setUploaderOpen((isOpen)=>{
            onExpandToggled === null || onExpandToggled === void 0 ? void 0 : onExpandToggled(!isExpanded);
            return !isOpen;
        });
    }, [
        isExpanded,
        onExpandToggled
    ]);
    useEffect(()=>{
        setUploaderOpen(isExpanded);
    }, [
        isExpanded
    ]);
    const completedItems = items.filter((item)=>item.status === UploadStatus.completed);
    const abortedItems = useMemo(()=>showAborted ? items.filter((item)=>item.status === UploadStatus.aborted) : [], [
        showAborted,
        items
    ]);
    const erroredItems = items.filter((item)=>item.status === UploadStatus.failed);
    const ongoingItems = items.filter((item)=>[
            ...progressingStatuses,
            UploadStatus.paused
        ].includes(item.status));
    const completedCount = completedItems.length;
    const abortedCount = abortedItems.length;
    const erroredCount = erroredItems.length;
    const pausedCount = items.filter((upload)=>upload.status === UploadStatus.paused).length;
    const progressingCount = ongoingItems.length - pausedCount;
    const status = getSummaryTrackingStatus({
        erroredCount,
        progressingCount,
        pausedCount
    });
    const statusTitles = getStatusTitles({
        completedCount,
        abortedCount,
        erroredCount,
        pausedCount,
        progressingCount
    });
    var _statusTitles_status;
    const { title, subtitle } = (_statusTitles_status = statusTitles[status]) !== null && _statusTitles_status !== void 0 ? _statusTitles_status : {
        title: "",
        subtitle: ""
    };
    const completedItemsToDisplay = useMemo(()=>completedItems.map((item)=>/*#__PURE__*/ _jsx(FileStatusTrackingPanelListItemCompleted, {
                item: item
            }, item.id)), [
        completedItems
    ]);
    const erroredItemsToDisplay = useMemo(()=>erroredItems.map((item)=>/*#__PURE__*/ {
            var _item_error;
            return _jsx(FileStatusTrackingPanelListItemErrored, {
                item: item,
                onCancel: onCancelItem,
                onRetry: onRetryItem,
                error: (_item_error = item.error) !== null && _item_error !== void 0 ? _item_error : ""
            }, item.id);
        }), [
        erroredItems,
        onCancelItem,
        onRetryItem
    ]);
    const abortedItemsToDisplay = useMemo(()=>abortedItems.map((item)=>/*#__PURE__*/ _jsx(FileStatusTrackingPanelListItemAborted, {
                item: item
            }, item.id)), [
        abortedItems
    ]);
    const ongoingItemsToDisplay = useMemo(()=>ongoingItems.map((item)=>item.status === UploadStatus.paused ? /*#__PURE__*/ _jsx(FileStatusTrackingPanelListItemPaused, {
                item: item,
                onCancel: onCancelItem,
                onResume: onResumeItem
            }, item.id) : /*#__PURE__*/ _jsx(FileStatusTrackingPanelListItemProgressing, {
                item: item,
                onCancel: onCancelItem,
                onPause: onPauseItem
            }, item.id)), [
        onCancelItem,
        onPauseItem,
        onResumeItem,
        ongoingItems
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("rounded bg-surface-1 shadow-[0px_1px_3px_0px_#00000026,0px_0px_2px_0px_#00000040,0px_2px_8px_0px_#00000033]", className),
        style: {
            width: Math.min(minWidth, UPLOAD_PANE_WIDTH),
            ...style
        },
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsx(FileStatusPanelHeader, {
                disableCancel: disableCancel,
                isUploaderOpen: isUploaderOpen,
                onToggle: toggleUploaderContent,
                onPause: onPauseAll,
                onResume: onResumeAll,
                onCancel: onCancelAll,
                onDismiss: onDismiss,
                status: status,
                title: title,
                subtitle: subtitle
            }),
            /*#__PURE__*/ _jsx(FileStatusTrackingPanelList, {
                abortedItems: abortedItemsToDisplay,
                completedItems: completedItemsToDisplay,
                erroredItems: erroredItemsToDisplay,
                isUploaderOpen: isUploaderOpen,
                onRetryAll: onRetryAll,
                ongoingItems: ongoingItemsToDisplay
            })
        ]
    });
});
FileStatusTrackingPanel.displayName = "FileStatusTrackingPanel";
